import IndexedDBService from './indexedDBService';

/**
 * Print Job Queue Service
 * Manages print jobs in IndexedDB for offline-first printing
 */
class PrintJobQueue extends IndexedDBService {
  private readonly DB_NAME = 'printJobQueue';
  private readonly VERSION = 1;
  private readonly STORE_NAME = 'printJobs';

  constructor() {
    super('printJobQueue', 1);
  }

  protected setupObjectStores(db: IDBDatabase): void {
    // Print Jobs Store
    if (!db.objectStoreNames.contains(this.STORE_NAME)) {
      const store = db.createObjectStore(this.STORE_NAME, {
        keyPath: 'id',
        autoIncrement: true,
      });
      store.createIndex('status', 'status', { unique: false });
      store.createIndex('jobType', 'jobType', { unique: false });
      store.createIndex('createdAt', 'createdAt', { unique: false });
      store.createIndex('orderId', 'orderId', { unique: false });
      store.createIndex('sessionId', 'sessionId', { unique: false });
    }
  }

  /**
   * Add a print job to the queue
   */
  async addPrintJob(job: {
    orderId?: string;
    sessionId?: string;
    jobType: 'receipt' | 'kitchen' | 'session' | 'temporary_bill' | 'cart_items' | 'sales_report';
    orderData?: any;
    sessionData?: any;
    cartItems?: any[];
    cartItemsData?: any[];
    reportData?: any;
    stationStartTime?: Date;
    endTime?: Date;
    storeData: any;
    printerQuery: {
      api_key?: string;
      print_qr_receipt?: boolean;
    };
    isKitchenSlip?: boolean;
    kitchenSlipWithPrice?: boolean;
    cutbyItem?: boolean;
    cutbyCategory?: boolean;
    isTableOrder?: boolean;
    seatTableName?: string;
    tableMarkPlacement?: string;
    tableNote?: string;
    waiterSlip?: boolean;
    waiterSlipCut?: string;
    cupLabel?: boolean;
    cupLabelPaper?: string;
    bagLabel?: boolean;
    deliverySlip?: boolean;
    isDeliveryOrder?: boolean;
    guestName?: string;
    orderNumber?: string;
    deliveryAddress?: unknown;
    status?: 'pending' | 'processing' | 'completed' | 'failed';
    createdAt?: number;
    processedAt?: number;
    retries?: number;
    error?: string;
  }): Promise<number> {
    await this.init();
    
    const storeId = String(
      (job as { storeId?: string }).storeId
      || (job.storeData as { id?: string } | undefined)?.id
      || ""
    ).trim();
    const jobData = {
      ...job,
      storeId,
      status: job.status || 'pending',
      createdAt: job.createdAt || Date.now(),
      processedAt: job.processedAt || null,
      retries: job.retries || 0,
      error: job.error || null,
    };

    // Use add to get the auto-generated ID
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.STORE_NAME], 'readwrite');
      const store = transaction.objectStore(this.STORE_NAME);
      const request = store.add(jobData);

      request.onsuccess = () => {
        resolve(request.result as number);
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get all pending print jobs (excludes processing and completed jobs)
   */
  async getPendingJobs(): Promise<any[]> {
    await this.init();
    // Only get jobs with status 'pending' - processing jobs are excluded by the index query
    const pendingJobs = await this.queryByIndex(this.STORE_NAME, 'status', 'pending');
    const scope = typeof localStorage !== "undefined" ? String(localStorage.getItem("selectedStore") || "").trim() : "";
    // Double filter to ensure no processing jobs slip through
    return pendingJobs.filter((job: any) => {
      if (job.status !== 'pending' || !scope) return false;
      const jobStore = String(job.storeId || job.storeData?.id || "").trim();
      return !jobStore || jobStore === scope;
    });
  }

  /**
   * Get all print jobs by status
   */
  async getJobsByStatus(status: 'pending' | 'processing' | 'completed' | 'failed'): Promise<any[]> {
    await this.init();
    return await this.queryByIndex(this.STORE_NAME, 'status', status);
  }

  /**
   * Get a print job by ID
   */
  async getPrintJob(id: number): Promise<any> {
    await this.init();
    return await this.get(this.STORE_NAME, id);
  }

  /**
   * Update a print job
   */
  async updatePrintJob(id: number, updates: Partial<any>): Promise<void> {
    await this.init();
    const job = await this.get(this.STORE_NAME, id);
    if (job) {
      await this.put(this.STORE_NAME, {
        ...job,
        ...updates,
      });
    }
  }

  /**
   * Mark a print job as completed
   * Only marks if job is currently processing (prevents duplicate completion)
   * Returns true if successfully marked, false if job was not in processing status
   * ATOMIC: Uses a single transaction to read and write, preventing race conditions
   */
  async markJobCompleted(id: number): Promise<boolean> {
    await this.init();
    
    // CRITICAL: Use a single transaction for atomic read-check-write
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.STORE_NAME], 'readwrite');
      const store = transaction.objectStore(this.STORE_NAME);
      
      const getRequest = store.get(id);
      
      getRequest.onsuccess = () => {
        const job = getRequest.result;
        
        // Only mark as completed if job is currently processing
        if (!job || job.status !== 'processing') {
          console.log(`[REPEAT DEBUG] [printJobQueue] ATOMIC CHECK FAILED: Cannot mark job ${id} as completed - current status: ${job?.status}`);
          resolve(false);
          return;
        }
        
        // Update status in the same transaction
        const updatedJob = {
          ...job,
          status: 'completed',
          processedAt: Date.now(),
          error: null,
        };
        
        const putRequest = store.put(updatedJob);
        
        putRequest.onsuccess = () => {
          console.log(`[REPEAT DEBUG] [printJobQueue] ATOMIC SUCCESS: Job ${id} marked as completed`);
          resolve(true);
        };
        
        putRequest.onerror = () => {
          console.error(`[REPEAT DEBUG] [printJobQueue] ATOMIC ERROR: Failed to update job ${id}`, putRequest.error);
          reject(putRequest.error);
        };
      };
      
      getRequest.onerror = () => {
        console.error(`[REPEAT DEBUG] [printJobQueue] ATOMIC ERROR: Failed to get job ${id}`, getRequest.error);
        reject(getRequest.error);
      };
    });
  }

  /**
   * Mark a print job as failed
   */
  async markJobFailed(id: number, error: string, retry: boolean = true): Promise<void> {
    const job = await this.getPrintJob(id);
    const retries = (job?.retries || 0) + 1;
    const newStatus = retry && retries < 3 ? 'pending' : 'failed';
    
    console.log(`[REPEAT DEBUG] [printJobQueue] Marking job ${id} as failed. Retries: ${retries}, New status: ${newStatus}`);
    
    await this.updatePrintJob(id, {
      status: newStatus,
      error: error,
      retries: retries,
      processedAt: Date.now(),
    });
    
    // Verify status after update
    const verifyJob = await this.getPrintJob(id);
    console.log(`[REPEAT DEBUG] [printJobQueue] Job ${id} status after failure: ${verifyJob?.status}`);
    if (newStatus === "failed") {
      import("../desktop/osNotify")
        .then((mod) => mod.notifyPrinterProblem())
        .catch(() => {});
    }
  }

  /**
   * Mark a print job as processing
   * Only marks if job is currently pending (prevents duplicate processing)
   * Returns true if successfully marked, false if job was already processing/completed
   * ATOMIC: Uses a single transaction to read and write, preventing race conditions
   */
  async markJobProcessing(id: number): Promise<boolean> {
    await this.init();
    
    // CRITICAL: Use a single transaction for atomic read-check-write
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.STORE_NAME], 'readwrite');
      const store = transaction.objectStore(this.STORE_NAME);
      
      const getRequest = store.get(id);
      
      getRequest.onsuccess = () => {
        const job = getRequest.result;
        
        // Only mark as processing if job is currently pending
        if (!job || job.status !== 'pending') {
          console.log(`[REPEAT DEBUG] [printJobQueue] ATOMIC CHECK FAILED: Job ${id} status is ${job?.status}, not pending`);
          resolve(false);
          return;
        }
        
        // Update status in the same transaction
        const updatedJob = {
          ...job,
          status: 'processing',
        };
        
        const putRequest = store.put(updatedJob);
        
        putRequest.onsuccess = () => {
          console.log(`[REPEAT DEBUG] [printJobQueue] ATOMIC SUCCESS: Job ${id} marked as processing`);
          resolve(true);
        };
        
        putRequest.onerror = () => {
          console.error(`[REPEAT DEBUG] [printJobQueue] ATOMIC ERROR: Failed to update job ${id}`, putRequest.error);
          reject(putRequest.error);
        };
      };
      
      getRequest.onerror = () => {
        console.error(`[REPEAT DEBUG] [printJobQueue] ATOMIC ERROR: Failed to get job ${id}`, getRequest.error);
        reject(getRequest.error);
      };
    });
  }

  /**
   * Get failed jobs that can be retried
   */
  async getRetryableJobs(): Promise<any[]> {
    await this.init();
    const failedJobs = await this.getJobsByStatus('failed');
    return failedJobs.filter(job => job.retries < 3);
  }

  /**
   * Delete a print job
   */
  async deletePrintJob(id: number): Promise<void> {
    await this.init();
    await this.delete(this.STORE_NAME, id);
  }

  /**
   * Delete all completed jobs older than specified days
   */
  async cleanupOldJobs(daysOld: number = 7): Promise<void> {
    await this.init();
    const cutoffDate = Date.now() - (daysOld * 24 * 60 * 60 * 1000);
    const completedJobs = await this.getJobsByStatus('completed');
    
    for (const job of completedJobs) {
      if (job.processedAt && job.processedAt < cutoffDate) {
        await this.deletePrintJob(job.id);
      }
    }
  }

  /**
   * Get all print jobs (for debugging/admin)
   */
  async getAllJobs(): Promise<any[]> {
    await this.init();
    return await this.getAll(this.STORE_NAME);
  }
}

// Singleton instance
const printJobQueue = new PrintJobQueue();

export default printJobQueue;

